/* eslint-disable camelcase, max-classes-per-file */
import { loginService, APIBase } from '@agro1desenvolvimento/apis-js-package';
import dayjs from 'dayjs';
import { ENV } from '../../utilities/constants';
import { convertApiDate } from '../../utilities/date';
import { APIListResponse, APIAnaliseLaboratorio, StatusAPIMap } from './tetrazolio';
import imagensAPI, { ImagemAPI } from './imagensAPI';

class LaboratorioAPI extends APIBase {
  constructor() {
    super();

    let baseURL = 'https://homologacao-integrations.aqila.com.br';
    if (ENV === 'production') {
      baseURL = 'https://integrations.aqila.com.br';
    }

    this.initialize({ baseURL });

    this.request.interceptors.request.use((request) => {
      const headers = loginService.requestHeaders();
      delete headers['X-Scope'];
      request.headers = {
        ...headers,
        ...request.headers,
        'X-Scope': 'sementes',
      };
      return request;
    });
  }
}

const laboratorioAPI = new LaboratorioAPI();

/**
 * Converte uma data URL (base64) para Blob
 */
function dataURLtoBlob(dataURL: string): Blob {
  const arr = dataURL.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const bstr = atob(arr[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);

  for (let i = 0; i < n; i += 1) {
    u8arr[i] = bstr.charCodeAt(i);
  }

  return new Blob([u8arr], { type: mime });
}

export interface CanteiroAreiaItem {
  id: string;
  produtor: string;
  nAmostra: string;
  numeroRemessa: string;
  especie: string;
  dataEntrada: Date | null;
  status: string;
  amostra1: string;
  amostra2: string;
  media: string;
  lote: string;
  safra: string;
  nomeCientifico: string;
  cultivar: string;
  categoria: string;
  dataAmostragem: Date | null;
}

export interface CanteiroAreiaListParams {
  produtorId: string;
  page?: number;
  perPage?: number;
  search?: string;
  status?: string;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  dataInicio?: string;
  dataFim?: string;
}

export interface CanteiroAreiaListResponse {
  data: CanteiroAreiaItem[];
  meta: {
    currentPage: number;
    perPage: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface CanteiroAreiaAnalise {
  analista: string;
  data: Date;
  amostra1: number;
  amostra2: number;
  media: number;
  observacao: string;
  gerarBasRir: boolean;
}

export interface ImagemCanteiroAreia {
  id: string;
  url: string; // thumbnail para exibição em lista
  fullsizeUrl?: string; // imagem completa para modal
  nome: string;
  tipo: 'upload' | 'camera';
  timestamp: Date;
}

export interface CanteiroAreiaDetalhes extends CanteiroAreiaItem {
  dadosAnalise?: CanteiroAreiaAnalise;
  imagens?: ImagemCanteiroAreia[];
  concluidoApi?: boolean;
}

export interface ResultadosCAN {
  amostras: number[];
  media: number;
}

function convertAPIToInternal(apiData: APIAnaliseLaboratorio): CanteiroAreiaItem {
  let amostra1Value = '--';
  let amostra2Value = '--';
  let mediaValue = '--';

  // Recuperar resultados se existirem
  if (apiData.resultados && !Array.isArray(apiData.resultados)) {
    const resultados = apiData.resultados as { amostras?: number[]; media?: number };
    if (resultados.amostras && resultados.amostras.length >= 2) {
      amostra1Value = String(resultados.amostras[0]);
      amostra2Value = String(resultados.amostras[1]);
    }
    if (resultados.media !== undefined) {
      mediaValue = String(resultados.media);
    }
  }

  return {
    id: apiData.id,
    produtor: apiData.requerente.nome,
    nAmostra: apiData.amostra.numero_amostra
      || (apiData.amostra.id_amostra ? String(apiData.amostra.id_amostra) : null)
      || '--',
    numeroRemessa: apiData.coleta.numero_remessa || '--',
    especie: apiData.coleta.atividade || apiData.amostra.especie || apiData.amostra.cultivar,
    dataEntrada: convertApiDate(apiData.coleta.data_entrada),
    status: StatusAPIMap[apiData.status],
    amostra1: amostra1Value,
    amostra2: amostra2Value,
    media: mediaValue,
    lote: apiData.amostra.numero_lote,
    safra: apiData.coleta.safra,
    nomeCientifico: apiData.coleta.nome_cientifico || apiData.amostra.nome_cientifico || '--',
    cultivar: apiData.amostra.cultivar_longo || apiData.amostra.cultivar,
    categoria: apiData.amostra.categoria,
    dataAmostragem: convertApiDate(apiData.coleta.data_amostra),
  };
}

class CanteiroAreiaService {
  endpoint() {
    return `/${loginService.scope}/sementes/laboratorio/canteiro-areia`;
  }

  async list(params: CanteiroAreiaListParams): Promise<CanteiroAreiaListResponse> {
    const { page = 1, produtorId, search } = params;
    const queryParams: any = {
      page,
      produtor_id: produtorId,
      determinacao: 'CAN',
    };

    if (search) {
      queryParams.search_term = search;
    }

    if (params.status) {
      const statusMap: Record<string, string> = {
        Pendente: 'pendente',
        Iniciado: 'iniciado',
        Realizado: 'realizado',
      };
      const statusArray = params.status.split(',').map((s) => statusMap[s] || s);
      queryParams.status = statusArray.join(',');
    }

    if (params.dataInicio) {
      queryParams.data_inicio = params.dataInicio;
    }

    if (params.dataFim) {
      queryParams.data_fim = params.dataFim;
    }

    if (params.orderBy) {
      queryParams.order_by = params.orderBy;
    }

    if (params.orderDirection) {
      queryParams.order_direction = params.orderDirection;
    }

    const response = await laboratorioAPI.get<APIListResponse>(
      '/api/v1/agro1/laboratorio/analises_laboratorios',
      { params: queryParams },
    );

    const convertedData = response.data.data.map(convertAPIToInternal);

    return {
      data: convertedData,
      meta: {
        currentPage: response.data.page,
        perPage: response.data.per_page,
        totalItems: response.data.total,
        totalPages: response.data.total_pages,
      },
    };
  }

  async getById(id: string, produtorId: string): Promise<CanteiroAreiaDetalhes> {
    const response = await laboratorioAPI.get<APIListResponse>(
      '/api/v1/agro1/laboratorio/analises_laboratorios',
      { params: { id, produtor_id: produtorId } },
    );

    if (!response.data.data || response.data.data.length === 0) {
      throw new Error(`Análise de canteiro de areia com ID ${id} não encontrada`);
    }

    const apiData = response.data.data[0];

    let amostra1Value = '--';
    let amostra2Value = '--';
    let mediaValue = '--';

    // Recuperar resultados se existirem
    if (apiData.resultados && !Array.isArray(apiData.resultados)) {
      const resultados = apiData.resultados as { amostras?: number[]; media?: number };
      if (resultados.amostras && resultados.amostras.length >= 2) {
        amostra1Value = String(resultados.amostras[0]);
        amostra2Value = String(resultados.amostras[1]);
      }
      if (resultados.media !== undefined) {
        mediaValue = String(resultados.media);
      }
    }

    const detalhes: CanteiroAreiaDetalhes = {
      id: apiData.id,
      produtor: apiData.requerente.nome,
      nAmostra: apiData.amostra.numero_amostra
        || (apiData.amostra.id_amostra ? String(apiData.amostra.id_amostra) : null)
        || '--',
      numeroRemessa: apiData.coleta.numero_remessa || '--',
      especie: apiData.coleta.atividade || apiData.amostra.especie || apiData.amostra.cultivar,
      dataEntrada: convertApiDate(apiData.coleta.data_entrada),
      status: StatusAPIMap[apiData.status],
      amostra1: amostra1Value,
      amostra2: amostra2Value,
      media: mediaValue,
      lote: apiData.amostra.numero_lote,
      safra: apiData.coleta.safra,
      nomeCientifico: apiData.coleta.nome_cientifico || apiData.amostra.nome_cientifico || '--',
      cultivar: apiData.amostra.cultivar_longo || apiData.amostra.cultivar,
      categoria: apiData.amostra.categoria,
      dataAmostragem: convertApiDate(apiData.coleta.data_amostra),
      concluidoApi: (apiData as any).concluido_api || false,
    };

    // Verificar se há dados de análise (resultados preenchidos)
    if (
      apiData.resultados
      && !Array.isArray(apiData.resultados)
    ) {
      const resultados = apiData.resultados as { amostras?: number[]; media?: number };

      if (
        resultados.amostras
        && resultados.amostras.length >= 2
        && resultados.media !== undefined
      ) {
        // Usar data_instalacao se data_analise estiver vazia
        const dataAnalise = apiData.data_analise || apiData.data_instalacao;

        detalhes.dadosAnalise = {
          analista: apiData.analista?.id || '',
          data: dataAnalise ? dayjs(dataAnalise).toDate() : new Date(),
          amostra1: resultados.amostras[0],
          amostra2: resultados.amostras[1],
          media: resultados.media,
          observacao: apiData.observacao || '',
          gerarBasRir: apiData.gerar_bas_rir || false,
        };
      }
    }

    // Adicionar imagens da resposta da API
    if (apiData.imagens && apiData.imagens.length > 0) {
      detalhes.imagens = apiData.imagens.map((img) => ({
        id: img.erp_id,
        // Prioriza thumbnail da nova estrutura, depois URL legado
        url: img.urls?.thumbnail || img.url || '',
        fullsizeUrl: img.urls?.fullsize || img.url || '',
        nome: img.name,
        tipo: 'upload' as const,
        timestamp: new Date(),
      }));
    }

    return detalhes;
  }

  async submitAnalise(
    id: string,
    dados: CanteiroAreiaAnalise,
  ): Promise<void> {
    await laboratorioAPI.put(
      `/api/v1/agro1/laboratorio/analises_laboratorios/${id}`,
      {
        payload: {
          message: {
            analista: { id: dados.analista },
            data_analise: dayjs(dados.data).format('YYYY-MM-DD'),
            observacao: dados.observacao || '',
            gerar_bas_rir: dados.gerarBasRir,
          },
        },
      },
    );

    await laboratorioAPI.put(
      `/api/v1/agro1/laboratorio/analises_laboratorios/resultados/${id}`,
      {
        payload: {
          message: {
            resultados: {
              amostras: [dados.amostra1, dados.amostra2],
              media: dados.media,
            },
          },
        },
      },
    );
  }

  async concluirAnalise(id: string): Promise<void> {
    await laboratorioAPI.put(
      `/api/v1/agro1/laboratorio/analises_laboratorios/${id}/complete`,
    );
  }

  async buscarImagens(id: string): Promise<ImagemCanteiroAreia[]> {
    try {
      const imagensAPI_result = await imagensAPI.listarImagens(id);

      return imagensAPI_result.map((img: ImagemAPI) => ({
        id: img.erp_id,
        // Prioriza thumbnail da nova estrutura, depois URL legado, por último constrói manualmente
        url: img.urls?.thumbnail || img.url || imagensAPI.buildS3Url(img.file),
        fullsizeUrl: img.urls?.fullsize || img.url || imagensAPI.buildS3Url(img.file),
        nome: img.name,
        tipo: 'upload' as const,
        timestamp: new Date(img.created_at),
      }));
    } catch (error) {
      console.error('Erro ao buscar imagens:', error);
      return [];
    }
  }

  async adicionarImagem(
    id: string,
    imagem: Omit<ImagemCanteiroAreia, 'id' | 'timestamp'>,
  ): Promise<ImagemCanteiroAreia> {
    try {
      // Converter data URL para Blob
      const blob = dataURLtoBlob(imagem.url);
      const contentType = blob.type;

      // Obter URL pré-assinada
      const presignedData = await imagensAPI.getPresignedUrl(
        id,
        imagem.nome,
        contentType,
      );

      // Upload para S3
      await imagensAPI.uploadToS3(presignedData.url, blob, contentType);

      // Retornar imagem com o ID do registro criado pela API
      return {
        id: presignedData.id, // Usar o ID do registro retornado pela API
        url: imagem.url, // Manter URL local por enquanto
        nome: presignedData.filename,
        tipo: imagem.tipo,
        timestamp: new Date(),
      };
    } catch (error) {
      console.error('Erro ao adicionar imagem:', error);
      throw error;
    }
  }

  async removerImagem(_id: string, imagemId: string): Promise<void> {
    try {
      await imagensAPI.removerImagem(imagemId);
    } catch (error) {
      console.error('Erro ao remover imagem:', error);
      throw error;
    }
  }
}

export default new CanteiroAreiaService();
